import { useEffect, useMemo, useState } from 'react'

import { AskAiPanel } from '~/components/ask-ai-panel'
import { CameraWay } from '~/components/camera-way'
import { EMPTY_AI_STATE, aiDirty, type AiState } from '~/lib/ai-state'
import { FoodDials } from '~/components/food-dials'
import { FoodFormDialog } from '~/components/food-form-dialog'
import { FoodWizardDialog } from '~/components/food-wizard-dialog'
import { RecipeFormDialog } from '~/components/recipe-form-dialog'
import { FoodSearch, MyFoodsList, MyMealsList } from '~/components/food-search'
import { FoodSheet } from '~/components/food-sheet'
import { ChevronLeftIcon, MealIcon, PlusIcon, RecipeIcon } from '~/components/icons'
import { Tooltip } from '~/components/ui/tooltip'
import type { LoggingWay } from '~/components/logging-ways'
import { MealPage } from '~/components/meal-page'
import { NutrientFields } from '~/components/nutrient-fields'
import { EMPTY_NUTRIENT_TEXT, nutrientsTyped } from '~/lib/nutrient-text'
import { useFoodLogger } from '~/components/use-food-logger'
import { WhenPill } from '~/components/when-pill'
import {
  dayTotal,
  QUICK_ADD_NAME,
  nowTime,
  type Amount,
  type Entry,
  type Food,
  type GoalOnDay,
  type Meal,
  type MealLog,
  mealOfLog,
  type NutritionData,
} from '~/data/nutrition'
import { askingToLog } from '~/data/food-store'
import { useFocusOnMount } from '~/lib/focus-on-mount'

/**
 * One way of finding something to log, standing in the panel under the header's field — the
 * nutrition spec, Logging an entry. The ways are the glyphs along that field, and the field
 * itself is another of them: whichever is chosen, what it offers stands in the same panel
 * rather than in a page put up over the diary.
 *
 * What a way leads to stands here too. Pressing a food opens it on its own page inside the
 * panel, where its amount is chosen and it is logged; leaving that page leaves the way as it
 * was, ready for the next.
 */
export type PanelWay = 'search' | LoggingWay

/** What there is in the panel to lose, by what it is — see `LogFoodPanel`'s `onDirty`. */
export type PanelDirt = 'typed' | 'asked' | 'plate'

interface PanelProps {
  day: string
  /** The day's entries, for what the day would have left. */
  entries: readonly Entry[]
  goal: GoalOnDay | null
  /** The diary, for the calendar a logging day is picked on. */
  data: NutritionData | null
  /** The one way this panel is showing, searching being one of them. */
  way: PanelWay
  /** What has been typed in the field, which is the search's question. */
  typed?: string | undefined
  /** Something was logged, or the way gave up: the panel has done what it was opened for. */
  onDone: () => void
  /**
   * What there is in here to lose, and null where there is nothing: a quick entry begun, or a
   * question for the AI. A press outside the panel is a press the panel never sees, so whatever
   * holds it is the one that has to ask before it goes — and it has to know which it is asking
   * about, since the two are not the same act.
   */
  onDirty: (dirty: PanelDirt | null) => void
}

export function LogFoodPanel(props: PanelProps) {
  const { day, entries, goal, data, way, onDone } = props
  /*
   * The plate for somebody else is the one way that is a whole page, so it is the one kept
   * here rather than inside the way that shows: made the first time sending is chosen, and
   * kept, hidden, while another way is looked at — what is on it is still on it on coming
   * back, the way a quick entry's words are. It goes with the panel.
   */
  const [plateOpened, setPlateOpened] = useState(way === 'send')
  const [plateAtStake, setPlateAtStake] = useState(false)
  if (way === 'send' && !plateOpened) {
    setPlateOpened(true)
  }
  return (
    <>
      {/*
        Putting a plate together for somebody else — the nutrition spec, Sending food. The
        meal's page, which is what a list of foods in amounts already is, drawn into the panel
        as every way is. No fourth composer was built for this: what makes it a plate rather
        than a meal is that its key sends, that it asks for no name, and that leaving it once
        it has been sent asks nothing.
      */}
      {plateOpened && (
        <div hidden={way !== 'send'} className="flex min-h-0 flex-1 flex-col">
          <MealPage
            meal={null}
            exists={false}
            sending
            inPanel
            day={day}
            entries={entries}
            goal={goal}
            data={data}
            onClose={onDone}
            onLogged={onDone}
            onAtStake={setPlateAtStake}
          />
        </div>
      )}
      <WayPanel {...props} plateAtStake={plateAtStake} />
    </>
  )
}

function WayPanel({
  day,
  entries,
  goal,
  data,
  way,
  typed,
  onDone,
  onDirty,
  plateAtStake,
}: PanelProps & {
  /** Whether the plate kept beside this holds something not yet sent. */
  plateAtStake: boolean
}) {
  const [quickName, setQuickName] = useState(QUICK_ADD_NAME)
  const [quickNutrients, setQuickNutrients] = useState(EMPTY_NUTRIENT_TEXT)
  /** When what is logged lands: the day the diary is on and now, changed on each page's pill. */
  const [when, setWhen] = useState(() => ({ day, time: nowTime() }))
  /*
   * What the lists here are placed for — `/specs/features/food-order.spec.md`: the day being
   * logged onto and the time on it. Made again only where that moment itself is changed, so a
   * list is never re-placed under the hand of somebody reading down it.
   */
  const asking = useMemo(() => askingToLog(when.day, when.time), [when.day, when.time])
  const [ai, setAi] = useState<AiState>(EMPTY_AI_STATE)
  /**
   * Whether the camera has been looked through — a photo taken or picked, or words chosen —
   * after which asking is about the description; back goes to the lens again.
   */
  const [pastLens, setPastLens] = useState(false)
  /** A food being made in the middle of logging, from what is known of it so far. */
  const [creating, setCreating] = useState<{ name?: string; barcode?: string } | null>(null)
  /** A food being made from the account's own, by hand or off its packet. */
  const [making, setMaking] = useState(false)
  /** A recipe being made from the account's own. */
  const [makingRecipe, setMakingRecipe] = useState(false)
  /** A meal being made from the account's own, open on its empty page. */
  const [makingMeal, setMakingMeal] = useState(false)
  /** A meal opened onto its page — pressing one opens it rather than picking it. */
  const [openedMeal, setOpenedMeal] = useState<Meal | null>(null)
  /** Something logged without being kept, opened on the page as it was — still not a meal. */
  const [openedLog, setOpenedLog] = useState<MealLog | null>(null)
  /** A food opened on its own page, which is where its amount is chosen and it is logged. */
  const [about, setAbout] = useState<{ food: Food; mine: boolean; last: Amount | null } | null>(
    null,
  )
  // Logging a food is the same act wherever the food was found — see `useFoodLogger`.
  const logger = useFoodLogger()

  const quickTyped = nutrientsTyped(quickNutrients)
  const dirty: PanelDirt | null = aiDirty(ai)
    ? 'asked'
    : // The name it opened on is not something typed, so it is not something to lose.
      quickName.trim() !== QUICK_ADD_NAME ||
        Object.values(quickNutrients).some((value) => value !== '')
      ? 'typed'
      : plateAtStake
        ? 'plate'
        : null
  const rest = dayTotal(entries)

  // Said upward as it changes, and unsaid as the way goes, so nothing guards an empty panel.
  useEffect(() => {
    onDirty(dirty)
    return () => {
      onDirty(null)
    }
  }, [dirty, onDirty])

  const setDay = (next: string) => {
    setWhen((held) => ({ ...held, day: next }))
  }
  const setTime = (next: string) => {
    setWhen((held) => ({ ...held, time: next }))
  }

  /** A food logged from its page: the page is left and the way stands, for the next. */
  const logFood = (food: Food, amount: Amount) => {
    logger.logFood(food, amount, when)
    setAbout(null)
  }

  /** A quick entry: a name and the nutrients typed outright, logged as an entry of no food. */
  const logQuick = () => {
    if (quickName.trim() === '' || quickTyped === null) {
      return
    }
    logger.logEntries([
      {
        day: when.day,
        time: when.time,
        name: quickName.trim(),
        food: null,
        amount: { value: 1, unit: 'portion', portion: 'serving' },
        nutrients: quickTyped,
      },
    ])
    onDone()
  }

  /** A food pressed, wherever it stands: it opens on its own page, on the amount the list knew. */
  const openFood = (food: Food, last: Amount | null = null, mine?: boolean) => {
    setAbout({ food, mine: mine ?? food.source === 'own', last })
  }

  /*
   * A page opened out of a way — a food, a meal, something being made — opens over the page,
   * the way every page about a thing does, with the way it came from standing in the panel
   * behind it. The panel is where a thing is found; what is found is read at the size a page
   * wants rather than at the size a dropdown is.
   */
  const opened = (
    <>
      {about !== null && (
        <FoodSheet
          food={about.food}
          mine={about.mine}
          lastAmount={about.last}
          logging={{
            day: when.day,
            entries,
            goal,
            data,
            time: when.time,
            onDay: setDay,
            onTime: setTime,
            onLog: (amount) => {
              logFood(about.food, amount)
            },
          }}
          onClose={() => {
            setAbout(null)
          }}
        />
      )}
      {openedMeal !== null && (
        <MealPage
          key={openedMeal.id}
          meal={openedMeal}
          exists
          day={when.day}
          entries={entries}
          goal={goal}
          data={data}
          time={when.time}
          onClose={() => {
            setOpenedMeal(null)
          }}
          onLogged={onDone}
          onDeleted={() => {
            setOpenedMeal(null)
          }}
        />
      )}
      {openedLog !== null && (
        <MealPage
          key={openedLog.id}
          meal={mealOfLog(openedLog)}
          exists={false}
          unkeptLogId={openedLog.id}
          day={when.day}
          entries={entries}
          goal={goal}
          data={data}
          time={when.time}
          onClose={() => {
            setOpenedLog(null)
          }}
          onLogged={onDone}
          onKept={(kept) => {
            setOpenedLog(null)
            setOpenedMeal(kept)
          }}
        />
      )}
      {makingMeal && (
        <MealPage
          meal={null}
          exists={false}
          day={when.day}
          entries={entries}
          goal={goal}
          data={data}
          time={when.time}
          onClose={() => {
            setMakingMeal(false)
          }}
          onLogged={onDone}
          onKept={(kept) => {
            setMakingMeal(false)
            setOpenedMeal(kept)
          }}
        />
      )}
      {making && (
        <FoodWizardDialog
          onClose={() => {
            setMaking(false)
          }}
          onSaved={(food) => {
            setMaking(false)
            setAbout({ food, mine: true, last: null })
          }}
        />
      )}
      {makingRecipe && (
        <RecipeFormDialog
          onClose={() => {
            setMakingRecipe(false)
          }}
          onSaved={(food) => {
            setMakingRecipe(false)
            setAbout({ food, mine: true, last: null })
          }}
        />
      )}
      {creating !== null && (
        <FoodFormDialog
          seed={creating}
          onClose={() => {
            setCreating(null)
          }}
          onSaved={(food) => {
            setCreating(null)
            setAbout({ food, mine: true, last: null })
          }}
        />
      )}
    </>
  )

  /*
   * The quick entry: a form of its own, scrolling under a foot that stays where it is. When it
   * lands and the key that logs it are the way's chrome rather than the last thing on the
   * form, so they stand there whatever is scrolled beneath them.
   */
  if (way === 'quick') {
    return (
      <form
        className="relative flex min-h-0 flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault()
          logQuick()
        }}
      >
        {opened}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3 pb-16">
          <div className="block min-w-0">
            <label htmlFor="quick-add-name" className="mb-1 block text-sm text-ink-muted">
              Name
            </label>
            <QuickNameField value={quickName} onValue={setQuickName} />
          </div>
          {/* What the figures typed come to, against the day, as they are typed. */}
          <FoodDials comesTo={quickTyped} dayTotalWithout={rest} goal={goal} />
          <NutrientFields text={quickNutrients} onText={setQuickNutrients} />
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center gap-3 bg-gradient-to-t from-surface-overlay via-surface-overlay/95 to-transparent px-3 pt-10 pb-3 [&>*]:pointer-events-auto">
          <WhenPill day={when.day} time={when.time} data={data} onDay={setDay} onTime={setTime} />
          <span className="flex-1" />
          <button
            type="submit"
            disabled={quickName.trim() === '' || quickTyped === null}
            className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink disabled:opacity-50"
          >
            Log
          </button>
        </div>
      </form>
    )
  }

  // The plate stands beside this rather than in it — see `LogFoodPanel`.
  if (way === 'send') {
    return null
  }

  // The camera fills the panel rather than sitting in its margins.
  if (way === 'camera' && !pastLens) {
    return (
      <div className="flex min-h-0 flex-1 flex-col p-2">
        {opened}
        <CameraWay
          onPhoto={(image) => {
            setAi({ ...ai, image })
            setPastLens(true)
          }}
          onWords={() => {
            setPastLens(true)
          }}
          // A scanned code opens the food carrying it, on its own page.
          onFound={openFood}
          onCreate={(barcode) => {
            setCreating({ barcode })
          }}
          onGiveUp={onDone}
        />
      </div>
    )
  }

  /* Each of the account's own lists makes one of its kind, from the top of the list. */
  const makes =
    way === 'foods'
      ? {
          label: 'New food',
          Icon: PlusIcon,
          press: () => {
            setMaking(true)
          },
        }
      : way === 'recipes'
        ? {
            label: 'New recipe',
            Icon: RecipeIcon,
            press: () => {
              setMakingRecipe(true)
            },
          }
        : way === 'meals'
          ? {
              label: 'New meal',
              Icon: MealIcon,
              press: () => {
                setMakingMeal(true)
              },
            }
          : null

  return (
    <div className="relative min-h-0 flex-1 overflow-y-auto p-3">
      {opened}
      {/*
        Making one of your own stands over the list rather than above it: a glyph in the
        panel's own corner, taking none of the room the list wants, with what it is called
        said under it while the pointer rests there.
      */}
      {makes !== null && (
        <div className="absolute top-2 right-3 z-10">
          <Tooltip label={makes.label}>
            <button
              type="button"
              aria-label={makes.label}
              className="rounded-full bg-surface-overlay/80 p-2 text-ink-muted backdrop-blur transition-colors hover:bg-hover hover:text-ink"
              onClick={makes.press}
            >
              <makes.Icon aria-hidden className="size-6" />
            </button>
          </Tooltip>
        </div>
      )}
      <div className="space-y-4">
        {way === 'camera' && pastLens && (
          <>
            {/* Past the lens, the way back to it stands where a page's own back would. */}
            <button
              type="button"
              aria-label="Back"
              className="icon-btn -ml-1"
              onClick={() => {
                setPastLens(false)
              }}
            >
              <ChevronLeftIcon aria-hidden className="size-7" />
            </button>
            <AskAiPanel
              state={ai}
              onState={setAi}
              day={when.day}
              // The day and time logging was opened on, which is what an answer lands at.
              time={when.time}
              entries={entries}
              goal={goal}
              onClose={onDone}
              onLogged={onDone}
              // Kept, the asking is spent and the page under it is the kept meal's own.
              onKept={(kept) => {
                setAi(EMPTY_AI_STATE)
                setOpenedMeal(kept)
              }}
            />
          </>
        )}
        {way === 'search' && (
          <FoodSearch
            typed={typed ?? ''}
            asking={asking}
            picked={new Set()}
            onPick={openFood}
            onMeal={setOpenedMeal}
            onLog={setOpenedLog}
            onCreate={(name) => {
              setCreating({ name })
            }}
          />
        )}
        {way === 'meals' && <MyMealsList onMeal={setOpenedMeal} alone asking={asking} />}
        {(way === 'foods' || way === 'recipes') && (
          <MyFoodsList picked={new Set()} only={way} onPick={openFood} asking={asking} />
        )}
      </div>
    </div>
  )
}

/**
 * The quick add's name, which takes the cursor as its tab opens — and takes what is already in
 * it with them, so the name the form opened on is typed over by whoever wants their own and
 * kept as it stands by whoever does not.
 */
function QuickNameField({ value, onValue }: { value: string; onValue: (next: string) => void }) {
  const field = useFocusOnMount<HTMLInputElement>()
  return (
    <input
      ref={field}
      onFocus={(event) => {
        event.target.select()
      }}
      id="quick-add-name"
      aria-label="Name"
      value={value}
      onChange={(event) => {
        onValue(event.target.value)
      }}
      className="w-full rounded-2xl bg-surface p-3 text-lg outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
    />
  )
}
